import {
  GTProp,
  HTML_CONTENT_PROPS,
  JsxChild,
  JsxChildren,
  JsxElement,
  Variable,
  VariableType as GTVariableType,
} from '@generaltranslation/format/types';
import { TransformState } from '../../state/types';
import * as t from '@babel/types';
import type { NodePath } from '@babel/traverse';
import { validateIdentifier } from './validation/validateIdentifier';
import { validateTemplateLiteral } from './validation/validateTemplateLiteral';
import { validateChildrenElement } from './validation/validateChildrenElement';
import { getCalleeNameFromJsxExpressionParam } from './utils/getCalleeNameFromJsxExpressionParam';
import { getTrackedVariable } from '../getTrackedVariable';
import { isReactComponent } from '../../utils/constants/react/helpers';
import { REACT_COMPONENTS } from '../../utils/constants/react/constants';
import { validateChildrenFromArgs } from './validation/validateChildrenFromArgs';
import { IdObject } from './utils/id';
import { VariableType } from '../../state/ScopeTracker';
import {
  defaultVariableNames,
  getVariableName,
  isBranchComponent,
  isDeriveComponent,
  isGTComponent,
  isVariableComponent,
  minifyCanonicalName,
} from '../../utils/constants/gt/helpers';
import { validateStringLiteralPropertyFromArg } from './validation/validateStringLiteralPropertyFromArg';
import { GT_COMPONENT_TYPES } from '../../utils/constants/gt/constants';
import { getBranchComponentParameters } from './utils/getBranchComponentParameters';
import { validateNameFieldForVarComponent } from './validation/validateNameFieldForVarComponent';
import { validateUnaryExpression } from './validation/validateUnaryExpression';
import {
  createErrorLocation,
  generateDynamicContentErrorMessage,
} from '../../utils/errors';
import { validateJsxCall } from './validation/validateJsxCall';
import {
  JsxValidationError,
  dynamicContentError,
  structuralError,
} from './errors';

/**
 * Given the children of a <T> component, constructs a JsxChildren object.
 * Takes an Expression path.
 *
 * ONLY does JsxChildren construction + validation, no further processing on any children.
 *
 * On invalid children, quit immediately.
 *
 * The node checks intentionally happen before navigating with .get(). This
 * keeps runtime validation behavior stable while preserving NodePath scope for
 * future derivation work.
 */
export function constructJsxChildren(
  childrenPath: NodePath<t.Expression> | undefined,
  state: TransformState,
  id: IdObject = new IdObject()
): {
  errors: JsxValidationError[];
  value?: JsxChildren;
  containsDerive?: boolean;
} {
  const errors: JsxValidationError[] = [];

  // Skip if no children
  if (!childrenPath) {
    return { errors, value: childrenPath };
  }

  const children = childrenPath.node;
  let value: JsxChildren | undefined;
  let containsDerive = false;
  if (t.isArrayExpression(children)) {
    // Handle ArrayExpression
    value = [];
    const elementPaths = childrenPath.get('elements');

    for (let i = 0; i < children.elements.length; i++) {
      const child = children.elements[i];
      // Validate child
      if (!validateChildrenElement(child)) {
        errors.push(
          dynamicContentError(
            generateDynamicContentErrorMessage() +
              (child && createErrorLocation(child))
          )
        );
        return { errors };
      }

      // Special children edge cases: nullLiteral, booleanLiteral
      if (t.isBooleanLiteral(child) || t.isNullLiteral(child)) {
        continue;
      }

      // Construct JsxChild
      const childPath = elementPaths[i] as NodePath<
        Exclude<t.Expression, t.ArrayExpression>
      >;
      const validation = constructJsxChild(childPath, state, id);
      errors.push(...validation.errors);
      if (errors.length > 0) {
        return { errors };
      }
      containsDerive ||= !!validation.containsDerive;
      // Skip if no value
      if (validation.value === undefined) continue;
      (value as JsxChild[]).push(validation.value);
    }
  } else {
    // Handle single child
    const validation = constructJsxChild(
      childrenPath as NodePath<Exclude<t.Expression, t.ArrayExpression>>,
      state,
      id
    );
    errors.push(...validation.errors);
    if (errors.length > 0) {
      return { errors };
    }
    containsDerive = !!validation.containsDerive;
    value = validation.value;
  }

  return { errors, value, containsDerive };
}

/**
 * Given an Expression path, constructs a JsxChild.
 * @returns { errors: string[]; value?: JsxChild }
 */
function constructJsxChild(
  childPath: NodePath<Exclude<t.Expression, t.ArrayExpression>>,
  state: TransformState,
  id: IdObject
): {
  errors: JsxValidationError[];
  value?: JsxChild;
  containsDerive?: boolean;
} {
  const errors: JsxValidationError[] = [];
  const child = childPath.node;
  let value: JsxChild | undefined;
  let containsDerive = false;

  if (t.isCallExpression(child)) {
    // Construct JsxElement
    const validation = constructJsxElement(
      childPath as NodePath<t.CallExpression>,
      state,
      id
    );
    errors.push(...validation.errors);
    if (errors.length > 0) {
      return { errors };
    }
    containsDerive = !!validation.containsDerive;
    value = validation.value;
  } else if (t.isStringLiteral(child)) {
    value = child.value;
  } else if (t.isTemplateLiteral(child)) {
    const validation = validateTemplateLiteral(child);
    errors.push(...validation.errors.map((msg) => dynamicContentError(msg)));
    if (errors.length > 0) {
      return { errors };
    }
    value = validation.value;
  } else if (t.isNumericLiteral(child)) {
    value = child.value.toString();
  } else if (t.isBooleanLiteral(child)) {
    value = child.value as unknown as JsxChild;
  } else if (t.isNullLiteral(child)) {
    value = null as unknown as JsxChild;
  } else if (t.isUnaryExpression(child)) {
    const validation = validateUnaryExpression(child);
    errors.push(...validation.errors.map((msg) => structuralError(msg)));
    if (errors.length > 0) {
      return { errors };
    }
    value = validation.value;
  } else if (t.isIdentifier(child)) {
    // <T>{name}</T> or <T>{undefined}</T>
    const validation = validateIdentifier(child, state);
    errors.push(...validation.errors.map((msg) => dynamicContentError(msg)));
    if (errors.length > 0) {
      return { errors };
    }
    value = validation.value;
  } else {
    // Other cases fail
    errors.push(
      dynamicContentError(
        generateDynamicContentErrorMessage() + createErrorLocation(child)
      )
    );
    return { errors };
  }

  return { errors, value, containsDerive };
}

/**
 * Given a CallExpression path, constructs a JsxChild.
 * Handles: Jsx(T, ...children)
 */
function constructJsxElement(
  callExprPath: NodePath<t.CallExpression>,
  state: TransformState,
  id: IdObject
): {
  errors: JsxValidationError[];
  value?: JsxElement | Variable;
  containsDerive?: boolean;
} {
  const errors: JsxValidationError[] = [];
  const callExpr = callExprPath.node;

  // Validate that this is a jsx call
  const jsxValidation = validateJsxCall(callExpr, state);
  errors.push(...jsxValidation.map((msg) => dynamicContentError(msg)));
  if (jsxValidation.length > 0) {
    return { errors };
  }

  // Increment id
  id.increment();

  // Get first argument
  if (callExpr.arguments.length === 0) {
    return { errors };
  }
  const firstArg = callExpr.arguments[0];
  if (!t.isExpression(firstArg)) {
    errors.push(
      structuralError(
        `Failed to construct JsxElement! First argument must be an expression` +
          createErrorLocation(callExpr.arguments[0])
      )
    );
    return { errors };
  }

  // Resolve canonical name
  const { namespaceName, functionName } =
    getCalleeNameFromJsxExpressionParam(firstArg);
  if (!functionName) {
    errors.push(
      structuralError(
        `Failed to construct JsxElement! First argument must be a valid function` +
          createErrorLocation(callExpr.arguments[0])
      )
    );
    return { errors };
  }

  // Get the canonical function name
  const { canonicalName, type } = getTrackedVariable(
    state.scopeTracker,
    namespaceName,
    functionName
  );

  // Handle variable components
  if (
    canonicalName &&
    type === 'generaltranslation' &&
    isVariableComponent(canonicalName)
  ) {
    const variableValidation = constructVariable(
      canonicalName,
      callExpr.arguments,
      id
    );
    errors.push(
      ...variableValidation.errors.map((msg) => structuralError(msg))
    );
    if (variableValidation.errors.length > 0) {
      return { errors };
    }
    return { errors, value: variableValidation.value! };
  }

  // Set the component name
  let componentName: string;
  const idNumber: number = id.get();
  if (canonicalName && type === 'generaltranslation') {
    // Handle GT components: <Var>, <Num>, <Currency>, etc.

    // Check that this is a gt component
    if (!isGTComponent(canonicalName)) {
      errors.push(
        structuralError(
          `Failed to construct JsxElement! ${canonicalName} is not a valid GT component` +
            createErrorLocation(callExpr.arguments[0])
        )
      );
      return { errors };
    }
    // Derive - opaque element, skip children validation
    // The compiler doesn't resolve Derive functions; the CLI handles that.
    // Surface containsDerive so the collection pass skips hash injection:
    // a Derive-containing <T> has one hash per resolved variant, which only
    // the runtime can compute.
    if (isDeriveComponent(canonicalName)) {
      return {
        errors,
        value: { t: canonicalName, i: idNumber },
        containsDerive: true,
      };
    }
    // Get the name of the component
    componentName = canonicalName;
  } else if (canonicalName && type === 'react') {
    // Handle fragment + special react components
    if (!isReactComponent(canonicalName)) {
      errors.push(
        structuralError(
          `Failed to construct JsxElement! ${canonicalName} is not a valid React component` +
            createErrorLocation(callExpr.arguments[0])
        )
      );
      return { errors };
    }
    // Get the name of the component
    componentName =
      canonicalName === REACT_COMPONENTS.Fragment
        ? `C${id.get()}`
        : functionName;
  } else {
    // Handle all other components: div, etc.
    componentName = functionName;
  }

  // Get children from args
  const childrenValidation = validateChildrenFromArgs(callExprPath);
  if (childrenValidation.errors.length > 0) {
    errors.push(
      ...childrenValidation.errors.map((msg) => structuralError(msg))
    );
    return { errors };
  }

  // Construct JsxChildren
  // For branching components (Branch/Plural), use id.copy() so children and
  // branch props start counting from the same base independently.
  const jsxChildrenValidation = constructJsxChildrenForJsxElement(
    childrenValidation.value,
    state,
    isBranchComponent(canonicalName ?? '') ? id.copy() : id
  );
  errors.push(...jsxChildrenValidation.errors);
  if (jsxChildrenValidation.errors.length > 0) {
    return { errors };
  }
  const children: JsxChildren | undefined = jsxChildrenValidation.value;

  // Construct GT Tag
  const tagValidation = constructGTProp(
    callExprPath,
    id,
    state,
    canonicalName,
    type
  );
  errors.push(...tagValidation.errors);
  if (tagValidation.errors.length > 0) {
    return { errors };
  }
  const tag: GTProp | undefined = tagValidation.value;

  // Return result
  const value: JsxElement = {
    t: componentName,
    i: idNumber,
    ...(tag !== undefined && { d: tag }),
    ...(children !== undefined && { c: children }),
  };
  return {
    errors,
    value,
    containsDerive: !!(
      jsxChildrenValidation.containsDerive || tagValidation.containsDerive
    ),
  };
}

/**
 * Construct JsxChildren for a JsxElement
 * This is slightly different from constructJsxChildren in how it handles nullLiteral and booleanLiteral
 */
function constructJsxChildrenForJsxElement(
  childrenPath: NodePath<t.Expression> | undefined,
  state: TransformState,
  id: IdObject
): {
  errors: JsxValidationError[];
  value?: JsxChildren;
  containsDerive?: boolean;
} {
  const errors: JsxValidationError[] = [];
  const children = childrenPath?.node;

  // Special children edge cases: nullLiteral, booleanLiteral
  if (t.isNullLiteral(children)) {
    return { errors, value: undefined as unknown as JsxChildren };
  }
  if (t.isBooleanLiteral(children)) {
    return {
      errors,
      value: (children.value || undefined) as unknown as JsxChildren,
    };
  }

  // Construct JsxChildren
  return constructJsxChildren(childrenPath, state, id);
}

/**
 * Given a canonical name, constructs a GTProp
 */
function constructGTProp(
  callExprPath: NodePath<t.CallExpression>,
  id: IdObject,
  state: TransformState,
  canonicalName?: string,
  type?: VariableType
): {
  errors: JsxValidationError[];
  value?: GTProp;
  containsDerive?: boolean;
} {
  const errors: JsxValidationError[] = [];
  const value: GTProp = {};
  let containsDerive = false;
  const args = callExprPath.node.arguments;

  // Validate Parameters
  if (args.length < 2) {
    errors.push(
      structuralError(
        'Failed to construct GTProp! Missing parameters' +
          createErrorLocation(args[0])
      )
    );
    return { errors };
  }
  const parameters = args[1];
  if (!t.isObjectExpression(parameters)) {
    errors.push(
      structuralError(
        'Failed to construct GTProp! Parameter field must be an object expression' +
          createErrorLocation(args[1])
      )
    );
    return { errors };
  }

  // For Branch and Plural, get the properties
  const parametersPath = callExprPath.get(
    'arguments'
  )[1] as NodePath<t.ObjectExpression>;

  if (
    canonicalName &&
    type === 'generaltranslation' &&
    isBranchComponent(canonicalName)
  ) {
    // Get the branching parameters
    const branchingParameters = getBranchComponentParameters(
      parametersPath as NodePath<t.ObjectExpression>,
      canonicalName
    );
    // Add branch component branches
    const branches = {} as Record<string, JsxChildren>;

    for (const [name, parameterPath] of branchingParameters) {
      const parameter = parameterPath.node;
      // Special exceptions for branches:
      if (t.isNullLiteral(parameter)) {
        branches[name] = null as unknown as JsxChildren;
        continue;
      } else if (t.isBooleanLiteral(parameter)) {
        branches[name] = parameter.value as unknown as JsxChildren;
        continue;
      }

      // Otherwise, construct the JsxChildren
      const validation = constructJsxChildren(parameterPath, state, id.copy());
      errors.push(...validation.errors);
      if (validation.errors.length > 0) {
        return { errors };
      }
      containsDerive ||= !!validation.containsDerive;
      if (validation.value === undefined) continue;
      branches[name] = validation.value;
    }

    if (Object.keys(branches).length > 0) {
      value['b'] = branches;
      value['t'] = canonicalName === GT_COMPONENT_TYPES.Branch ? 'b' : 'p';
    }
  } else {
    // Get the html content props
    Object.entries(HTML_CONTENT_PROPS).forEach(([prop, name]) => {
      const validation = validateStringLiteralPropertyFromArg(parameters, name);
      if (validation.errors.length > 0) {
        errors.push(...validation.errors.map((msg) => structuralError(msg)));
        return { errors };
      }
      if (validation.value === undefined) return;
      value[prop as keyof typeof HTML_CONTENT_PROPS] = validation.value;
    });
  }

  // Return result
  return {
    errors,
    value: Object.keys(value).length > 0 ? value : undefined,
    containsDerive,
  };
}

/**
 * Construct Variable
 */
function constructVariable(
  canonicalName: GT_COMPONENT_TYPES,
  args: (t.ArgumentPlaceholder | t.SpreadElement | t.Expression)[],
  id: IdObject
): { errors: string[]; value?: Variable } {
  const errors: string[] = [];
  // Validate Parameters
  if (args.length < 2) {
    errors.push(
      'Failed to construct GTProp! Missing parameters' +
        createErrorLocation(args[0])
    );
    return { errors };
  }
  const parameters = args[1];
  if (!t.isObjectExpression(parameters)) {
    errors.push(
      'Failed to construct GTProp! Parameter field must be an object expression' +
        createErrorLocation(args[1])
    );
    return { errors };
  }

  // Validate Parameters
  const nameValidation = validateNameFieldForVarComponent(parameters);
  errors.push(...nameValidation.errors);
  if (nameValidation.errors.length > 0) {
    return { errors };
  }
  const name = nameValidation.value;

  // Check for name field
  const value: Variable = {
    i: id.get(),
    k: getVariableName(
      canonicalName as keyof typeof defaultVariableNames,
      id.get(),
      name
    ),
    v: minifyCanonicalName(canonicalName) as GTVariableType,
  };

  return { errors, value };
}
